import { FormToFilling } from "../../types/new-form-filling/types";
import {normalizeToFloat} from '../../helpers/getNumberMask';
import { isValidCpfCnpjFormat } from '../../helpers/cpfCnpj';

export type returnType = {
  form?: boolean;
  success: boolean;
  item: string | null;
  subItem: string | null;
  chamado?: boolean;
  texto?: string | null;
  ocorrencia?: string | null;
  itemId?: string | null;
  subItemId?: number | null;
};

export default function CheckFilling(checklist: FormToFilling): returnType[] {
  const regexEmail = /^(?![.-])([A-Za-z0-9._%+-]+(?:(?<=.)[A-Za-z0-9._%+-]))@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  let returnList: returnType[] = [];

  for (const item of checklist.itens) {
    const subitensGroup = item.ocorrenciaMultipla
      ? (item.ocorrencias?.flatMap((o) => o.subitens) ?? [])
      : item.subitens;
    for (const subItem of subitensGroup) {
      let base: returnType = {
        success: false,
        item: item.nome as string,
        subItem: subItem.nome,
        itemId: item.id,
        subItemId: subItem.id,
      };

      const updateBase = (texto: string) => {
        if (base.texto) {
          base = { ...base, texto: `${base.texto}\n\n• ${texto}` };
        } else {
          base = { ...base, texto: `\n\n• ${texto}` };
        }
      };

      const checkChamadoForm = checklist?.ticketConfig?.mandatory && !checklist.chamados;
      if (checkChamadoForm) {
        returnList.push({ ...base, texto: "É obrigatório abrir um chamado neste formulário", form: true });
      }

      const foiPreenchido = (() => {
        switch (subItem.tipo) {
          case "avaliacao":
            return typeof subItem.nota === "number" && !isNaN(subItem.nota);
          case "unique":
          case "multiple":
          case "list":
            return !!(subItem.selecionados && subItem.selecionados.length > 0);
          case "assinatura":
            return !!subItem.assinatura;
          default:
            return !!(subItem.ocorrencia);
        }
      })();

      const deveValidar = subItem.obrigatoriedade?.preenchimento || foiPreenchido;
      if (!deveValidar) continue;

      // Avaliação
      if (subItem.tipo === "avaliacao") {
        const nota = subItem.nota;
        const threshold = subItem.threshold ?? 0;

        if (typeof nota !== "number" || isNaN(nota)) {
          updateBase('Sem preenchimento');
        }

        let status: "conforme" | "naoConforme" | "NA" = "conforme";
        if (nota === -1) status = "NA";
        else if (typeof nota === "number" && nota <= threshold) status = "naoConforme";
        const obrig = subItem.obrigatoriedade?.[status];

        if (obrig?.img && (!subItem.img || subItem.img.length === 0)) updateBase('É obrigatório ao menos uma imagem');
        if (obrig?.ocorrencia && (!subItem.ocorrencia || (subItem.ocorrencia as string)?.length === 0)) updateBase('É obrigatório adicionar um comentário');
        if (obrig?.obrigaArquivo && (!subItem.arquivos || subItem.arquivos.length === 0)) updateBase('É obrigatório enviar um arquivo');
        if (subItem.ticketConfig?.mandatory === status && (!subItem.chamados || subItem.chamados.length === 0)) {
          updateBase('É obrigatório abrir um chamado');
        }
        if (subItem.ticketConfig?.mandatory === "sempre" && (!subItem.chamados || subItem.chamados.length === 0)) {
          updateBase('É obrigatório abrir um chamado');
        }

        if (base.texto) returnList.push(base);
        continue;
      }

      if (subItem.tipo === "valorMedida") {
        const valor = subItem.ocorrencia;

        if (valor === undefined || valor === null || normalizeToFloat(valor)  === null || isNaN(normalizeToFloat(valor) as number)) {
          updateBase("Valor de medida inválido ou não preenchido");
        } else {
          const valMedida = normalizeToFloat(valor);
          if (valMedida !== null && valMedida !== undefined && !isNaN(valMedida)) {
            const minVal = normalizeToFloat(subItem.min);
            const maxVal = normalizeToFloat(subItem.max);

            if (minVal !== null && minVal !== undefined && !isNaN(minVal) && valMedida < minVal) {
              updateBase("Valor menor que o mínimo definido");
            }

            if (maxVal !== null && maxVal !== undefined && !isNaN(maxVal) && valMedida > maxVal) {
              updateBase("Valor maior que o máximo definido");
            }
          }
        }

        if (subItem.obrigatoriedade?.img && (!subItem.img || !subItem.img.length)) {
          updateBase("É obrigatório ao menos uma imagem");
        }

        if (subItem.obrigatoriedade?.ocorrencia && (!subItem?.obs || !subItem?.obs?.length)) {
          updateBase("É obrigatório adicionar um comentário");
        }

        if (subItem.obrigatoriedade?.obrigaArquivo && (!subItem.arquivos || !subItem.arquivos.length)) {
          updateBase("É obrigatório enviar um arquivo");
        }

        if (subItem.ticketConfig?.mandatory === "sempre" && (!subItem.chamados || !subItem.chamados.length)) {
          updateBase("É obrigatório abrir um chamado");
        }

        if (base.texto) returnList.push(base);
        continue;
      }

      // Funções de verificação comuns
      const checkImagem = () => subItem?.obrigatoriedade?.img && (!subItem.img || !subItem.img.length);
      const checkComentario = () =>
        subItem?.obrigatoriedade?.ocorrencia &&
        ((subItem.tipo !== "multiple" && !subItem.obs?.length) || (subItem.tipo === "multiple" && !(subItem.ocorrencia as string)?.length));
      const checkArquivo = () => subItem?.obrigatoriedade?.obrigaArquivo && (!subItem.arquivos || !subItem.arquivos.length);
      const checkChamado = () => subItem.ticketConfig?.mandatory === "sempre" && (!subItem.chamados || !subItem.chamados.length);

      // Campos de seleção
      if (["unique", "multiple", "list"].includes(subItem.tipo)) {
        if (subItem.obrigatoriedade?.preenchimento && !subItem.selecionados) updateBase("Sem preenchimento");
        if (checkImagem()) updateBase("É obrigatório ao menos uma imagem");
        if (checkComentario()) updateBase("É obrigatório adicionar um comentário");
        if (checkArquivo()) updateBase("É obrigatório enviar um arquivo");
        if (checkChamado()) updateBase("É obrigatório abrir um chamado");
        if (base.texto) returnList.push(base);
        continue;
      }

      // Assinatura
      if (subItem.tipo === "assinatura") {
        if (!subItem.assinatura) updateBase("Sem preenchimento");
        if (checkImagem()) updateBase("É obrigatório ao menos uma imagem");
        if (checkComentario()) updateBase("É obrigatório adicionar um comentário");
        if (checkArquivo()) updateBase("É obrigatório enviar um arquivo");
        if (checkChamado()) updateBase("É obrigatório abrir um chamado");
        if (base.texto) returnList.push(base);
        continue;
      }

      // Campos de texto e outros validáveis
      const val = subItem.ocorrencia;
      if (!val && subItem.tipo !== 'numero') {
        updateBase("Sem preenchimento");
        if (base.texto) returnList.push(base);
        continue;
      }

      switch (subItem.tipo) {
        case "cep":
          if ((val as string)?.length < 9) updateBase("CEP precisa ter ao menos 8 caracteres");
          break;
        case "cpfcnpj":
          if (!isValidCpfCnpjFormat(val as string)) {
            updateBase("CPF deve ter 11 números. CNPJ deve ter 12 caracteres alfanuméricos e 2 dígitos verificadores");
          }
          break;
        case "email":
          if (!regexEmail.test(val as string)) updateBase("E-mail inválido");
          break;
        case "numero":
          const numeroVal = normalizeToFloat(subItem.ocorrencia);

          // Considera 0 válido
          if (numeroVal === null || numeroVal === undefined || isNaN(numeroVal)) {
            updateBase("Número inválido ou não preenchido");
            break;
          }

          const minVal = normalizeToFloat(subItem.minNumberVal);
          const maxVal = normalizeToFloat(subItem.maxNumberVal);

          if (minVal !== null && minVal !== undefined && !isNaN(minVal) && numeroVal < minVal) {
            updateBase("Valor menor que o mínimo definido");
          }

          if (maxVal !== null && maxVal !== undefined && !isNaN(maxVal) && numeroVal > maxVal) {
            updateBase("Valor maior que o máximo definido");
          }
          break;
        case "placa":
          if ((val as string)?.length < 7) updateBase("Placa deve ter 7 caracteres");
          break;
        case "rg":
          if ((val as string)?.length !== 12) updateBase("RG deve ter 12 caracteres");
          break;
        case "telefone":
          if (![12, 13].includes((val as string)?.length)) updateBase("Telefone deve ter entre 10 e 11 números");
          break;
      }

      if (checkImagem()) updateBase("É obrigatório ao menos uma imagem");
      if (checkComentario()) updateBase("É obrigatório adicionar um comentário");
      if (checkArquivo()) updateBase("É obrigatório enviar um arquivo");
      if (checkChamado()) updateBase("É obrigatório abrir um chamado");
      if (base.texto) returnList.push(base);
    }
  }

  return returnList.length > 0 ? returnList : [{
    success: true,
    item: "",
    subItem: "",
    itemId: "",
  }];
}
